// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.
//
// D-POT-2 (Agent E) — the "Visit Billing Details" pop-up (asp-pot public/billing-modal.js, Sprint 41):
// the pre-submit step of the POT form that captures where the visit happened and how it bills.
//   • ZIP (5 digits) → lookup: fills State, fills City when the ZIP maps to one place, otherwise lists
//     the places for the nurse to pick (84065 → Riverton or Bluffdale).
//   • City type-ahead by name; picking a suggestion fills State (and ZIP when the city has one).
//   • Everything stays editable; any typed city is accepted.
// The lookup is injected (`lookupCities`) because OwlRx has no city/ZIP endpoint yet — see
// POT_CITY_LOOKUP in ../potApi.ts (GAP(D-POT-2)); without one the fields are plain free text.
// `VisitLocationFields` is shared with the billing tab's "Edit Visit" modal.
import { useEffect, useId, useRef, useState, type CSSProperties } from 'react'
import AppDialog from '../../../components/ui/AppDialog'
import { SelectField, type FieldOption } from '../../../components/ui/FormFields'
import { useToast } from '../../../components/ui/Toast'
import { kitSelectSx } from './billingShared'
import { POT_CITY_LOOKUP, type PotCityHit, type PotCityLookup, type PotVisitBillingDetails } from '../potApi'

export const US_STATES = ['AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DC', 'DE', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'PR', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'] as const
export const STATE_OPTIONS: FieldOption[] = [{ value: '', label: '—' }, ...US_STATES.map(s => ({ value: s, label: s }))]
export const VISIT_TYPE_OPTIONS: FieldOption[] = [
  { value: 'infusion', label: 'Infusion' },
  { value: 'admission', label: 'Admission / SOC' },
  { value: 'restart', label: 'Restart / Reauth' },
]
export const YES_NO_OPTIONS: FieldOption[] = [{ value: 'false', label: 'No' }, { value: 'true', label: 'Yes' }]

export interface VisitLocation { address: string; city: string; state: string; zip: string }
export const EMPTY_LOCATION: VisitLocation = { address: '', city: '', state: '', zip: '' }

// Older pop-up style (spec §1 "Older pop-up style"): 1.5px #C8CDD5 inputs, radius 10, teal #1A8B8B.
const F: CSSProperties = { width: '100%', padding: '10px 12px', border: '1.5px solid #C8CDD5', borderRadius: 10, fontSize: 14, boxSizing: 'border-box', fontFamily: 'inherit', background: '#fff', color: '#1A1D21' }
const L: CSSProperties = { display: 'block', fontSize: 11, fontWeight: 600, color: '#5A6270', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '.04em' }
const H: CSSProperties = { fontSize: 11, color: '#8E96A3', marginTop: 4, minHeight: 14 }
const selSx = { '& .MuiOutlinedInput-root': { borderRadius: '10px', fontSize: 13, fontFamily: 'inherit', background: '#fff' }, '& fieldset': { borderColor: '#C8CDD5', borderWidth: 1.5 }, '& .MuiSelect-select': { padding: '10px 12px' } }

const debounceMs = 200

/**
 * Address / City / State / ZIP with the ZIP → city/state auto-resolve and the city type-ahead.
 * Pass `look` to style the fields (`popup` = billing-modal.js look, `form` = billing.html `.fg` look).
 */
export function VisitLocationFields({ value, onChange, lookupCities = POT_CITY_LOOKUP, disabled, styles }: {
  value: VisitLocation
  onChange: (next: VisitLocation) => void
  lookupCities?: PotCityLookup | null
  disabled?: boolean
  styles?: { input?: CSSProperties; label?: CSSProperties; hint?: CSSProperties; selectSx?: typeof kitSelectSx }
}) {
  const id = useId()
  const input = styles?.input ?? F, label = styles?.label ?? L, hintStyle = styles?.hint ?? H
  const sx = styles?.selectSx ?? selSx
  const [cityHint, setCityHint] = useState('')
  const [zipHint, setZipHint] = useState('')
  const [suggestions, setSuggestions] = useState<PotCityHit[]>([])
  const cityRows = useRef<PotCityHit[]>([]), zipRows = useRef<PotCityHit[]>([])
  const seq = useRef(0)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const live = useRef(value); live.current = value
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current) }, [])

  const later = (fn: () => void) => { if (timer.current) clearTimeout(timer.current); timer.current = setTimeout(fn, debounceMs) }
  const patch = (p: Partial<VisitLocation>) => onChange({ ...live.current, ...p })

  // City typed → name suggestions (state / zip fill in when the nurse picks one)
  const onCity = (raw: string) => {
    patch({ city: raw })
    const q = raw.trim()
    if (q.length < 2 || /^\d+$/.test(q)) { setCityHint(/^\d{5}$/.test(q) ? 'That looks like a ZIP — use the ZIP field.' : ''); return }
    if (!lookupCities) { setCityHint(''); return }
    later(async () => {
      const my = ++seq.current
      try {
        const rows = await lookupCities(q + (live.current.state ? ', ' + live.current.state : ''))
        if (my !== seq.current) return
        cityRows.current = rows; setSuggestions(rows)
        setCityHint(rows.length ? '' : 'No match — you can still type the city name.')
      } catch { /* free text still works */ }
    })
  }
  // Picking a suggestion (change fires on the datalist pick) → fill state, and zip if unique
  const onCityCommit = () => {
    const v = live.current.city.trim().toLowerCase()
    const hit = [...cityRows.current, ...zipRows.current].find(r => r.city.toLowerCase() === v)
    if (!hit) return
    patch({ state: hit.state || live.current.state, zip: hit.zip && !live.current.zip ? hit.zip : live.current.zip })
    setCityHint('')
  }
  // ZIP typed → state + city (or the list of places this ZIP covers)
  const onZip = (raw: string) => {
    const z = raw.replace(/\D/g, '').slice(0, 5)
    patch({ zip: z })
    if (z.length < 5) { setZipHint(''); return }
    if (!lookupCities) { setZipHint('City lookup is not available yet in OwlRx — type the city and state.'); return }
    later(async () => {
      const my = ++seq.current
      try {
        const rows = await lookupCities(z)
        if (my !== seq.current) return
        zipRows.current = rows
        if (!rows.length) { setZipHint(`ZIP ${z} not found — type the city and state.`); return }
        setSuggestions(rows)
        if (rows.length === 1) {
          patch({ state: rows[0].state, city: rows[0].city })
          setCityHint(''); setZipHint(`${rows[0].city}, ${rows[0].state}`)
        } else {
          const current = live.current.city.trim().toLowerCase()
          const keep = rows.some(r => r.city.toLowerCase() === current)
          patch({ state: rows[0].state, city: keep ? live.current.city : '' })
          setZipHint(`ZIP ${z} covers ${rows.length} places`)
          setCityHint('Pick the city: ' + rows.map(r => r.city).join(' · '))
          if (!keep) document.getElementById(`${id}-city`)?.focus()
        }
      } catch { /* free text still works */ }
    })
  }

  return (
    <>
      <div style={{ marginBottom: 12 }}>
        <label htmlFor={`${id}-address`} style={label}>Visit Address</label>
        <input id={`${id}-address`} type="text" autoComplete="off" placeholder="Street address (optional)" maxLength={120} style={input}
          value={value.address} disabled={disabled} onChange={e => patch({ address: e.target.value })} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 10, marginBottom: 12 }}>
        <div>
          <label htmlFor={`${id}-city`} style={label}>City *</label>
          <input id={`${id}-city`} type="text" list={`${id}-cities`} autoComplete="off" placeholder="Type the city name" maxLength={80} style={input}
            aria-describedby={`${id}-cityhint`} value={value.city} disabled={disabled}
            onChange={e => onCity(e.target.value)} onBlur={onCityCommit} />
          <datalist id={`${id}-cities`}>
            {suggestions.map((c, i) => <option key={`${c.city}-${c.state}-${c.zip ?? ''}-${i}`} value={c.city}>{`${c.city}, ${c.state}${c.zip ? ' ' + c.zip : ''}`}</option>)}
          </datalist>
          <div id={`${id}-cityhint`} style={hintStyle}>{cityHint}</div>
        </div>
        <div>
          <label htmlFor={`${id}-state`} style={label}>State *</label>
          <SelectField id={`${id}-state`} size="small" fullWidth label="" options={STATE_OPTIONS} value={value.state} disabled={disabled}
            onChange={v => patch({ state: v })} sx={sx} inputProps={{ 'aria-label': 'State' }} />
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
        <div>
          <label htmlFor={`${id}-zip`} style={label}>ZIP Code</label>
          <input id={`${id}-zip`} type="text" inputMode="numeric" autoComplete="off" placeholder="5 digits" maxLength={5} pattern="[0-9]{5}" style={input}
            aria-describedby={`${id}-ziphint`} value={value.zip} disabled={disabled} onChange={e => onZip(e.target.value)} />
          <div id={`${id}-ziphint`} style={hintStyle}>{zipHint}</div>
        </div>
        <div />
      </div>
    </>
  )
}

/** Validation exactly as billing-modal.js: returns the toast text, or null when the details are good. */
export function visitBillingProblem(loc: VisitLocation): { msg: string; field: 'city' | 'state' | 'zip'; moveZip?: boolean } | null {
  const city = loc.city.replace(/\s+/g, ' ').trim(), zip = loc.zip.trim()
  if (/^\d{5}$/.test(city)) return { msg: 'Moved the ZIP to the ZIP field — now pick or type the city.', field: 'city', moveZip: true }
  if (city.length < 2) return { msg: 'Please enter the city visited', field: 'city' }
  if (!loc.state) return { msg: 'Please select the state', field: 'state' }
  if (zip && !/^\d{5}$/.test(zip)) return { msg: 'ZIP code must be 5 digits (or leave it blank)', field: 'zip' }
  return null
}

export default function VisitBillingModal({ open, onCancel, onSubmit, initial, lookupCities, busy, submitLabel = 'Submit Assessment' }: {
  open: boolean
  onCancel: () => void
  /** The nurse pressed Submit with valid details — the caller continues the submit sequence. */
  onSubmit: (details: PotVisitBillingDetails) => void
  initial?: Partial<PotVisitBillingDetails>
  lookupCities?: PotCityLookup | null
  busy?: boolean
  submitLabel?: string
}) {
  const toast = useToast()
  const id = useId()
  const [loc, setLoc] = useState<VisitLocation>(EMPTY_LOCATION)
  const [mileage, setMileage] = useState('')
  const [visitType, setVisitType] = useState<PotVisitBillingDetails['visitType']>('infusion')
  const [afterHours, setAfterHours] = useState('false')
  const [holiday, setHoliday] = useState('false')
  const initialRef = useRef(initial); initialRef.current = initial
  useEffect(() => {
    if (!open) return
    const initial = initialRef.current
    setLoc({ address: initial?.address ?? '', city: initial?.city ?? '', state: initial?.state ?? '', zip: initial?.zip ?? '' })
    setMileage(initial?.mileageMi ? String(initial.mileageMi) : '')
    setVisitType(initial?.visitType ?? 'infusion')
    setAfterHours(initial?.afterHours ? 'true' : 'false')
    setHoliday(initial?.holiday ? 'true' : 'false')
  }, [open])

  const submit = () => {
    const problem = visitBillingProblem(loc)
    if (problem) {
      if (problem.moveZip) setLoc(l => ({ ...l, zip: l.city.trim(), city: '' }))
      toast.show(problem.msg, 'error')
      return
    }
    onSubmit({
      address: loc.address.replace(/\s+/g, ' ').trim(), city: loc.city.replace(/\s+/g, ' ').trim(), state: loc.state, zip: loc.zip.trim(),
      mileageMi: parseFloat(mileage) || 0, visitType, afterHours: afterHours === 'true', holiday: holiday === 'true',
    })
  }

  return (
    <AppDialog open={open} onClose={onCancel} busy={busy} title="Visit Billing Details" maxWidth="sm"
      actions={<div style={{ display: 'flex', gap: 10, width: '100%' }}>
        <button type="button" onClick={submit} disabled={busy}
          style={{ flex: 1, padding: 12, border: 'none', borderRadius: 10, background: '#1A8B8B', color: '#fff', fontWeight: 600, fontSize: 14, cursor: 'pointer', fontFamily: 'inherit', opacity: busy ? .6 : 1 }}>
          {busy ? 'Submitting…' : submitLabel}
        </button>
        <button type="button" onClick={onCancel} disabled={busy}
          style={{ padding: '12px 16px', border: '1px solid #C8CDD5', borderRadius: 10, background: 'transparent', color: '#5A6270', cursor: 'pointer', fontSize: 13, fontFamily: 'inherit' }}>Cancel</button>
      </div>}>
      <p style={{ fontSize: 12, color: '#5A6270', margin: '0 0 16px' }}>Complete these fields before submitting. This information is used for invoicing.</p>
      <VisitLocationFields value={loc} onChange={setLoc} lookupCities={lookupCities} disabled={busy} />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12, marginTop: -12 }}>
        <div />
        <div>
          <label htmlFor={`${id}-mileage`} style={L}>Mileage (one way, agency → patient)</label>
          <input id={`${id}-mileage`} type="number" step="0.1" min="0" placeholder="0" style={F} value={mileage} disabled={busy} onChange={e => setMileage(e.target.value)} />
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 4 }}>
        <div>
          <label htmlFor={`${id}-type`} style={L}>Visit Type</label>
          <SelectField id={`${id}-type`} size="small" fullWidth label="" options={VISIT_TYPE_OPTIONS} value={visitType} disabled={busy}
            onChange={v => setVisitType(v as PotVisitBillingDetails['visitType'])} sx={selSx} inputProps={{ 'aria-label': 'Visit Type' }} />
        </div>
        <div>
          <label htmlFor={`${id}-ah`} style={L}>After-Hours?</label>
          <SelectField id={`${id}-ah`} size="small" fullWidth label="" options={YES_NO_OPTIONS} value={afterHours} disabled={busy}
            onChange={setAfterHours} sx={selSx} inputProps={{ 'aria-label': 'After-Hours?' }} />
        </div>
        <div>
          <label htmlFor={`${id}-hol`} style={L}>Holiday?</label>
          <SelectField id={`${id}-hol`} size="small" fullWidth label="" options={YES_NO_OPTIONS} value={holiday} disabled={busy}
            onChange={setHoliday} sx={selSx} inputProps={{ 'aria-label': 'Holiday?' }} />
        </div>
      </div>
    </AppDialog>
  )
}
